/*
 * Shared review card used by the Selection, Live, and Doc-suggestion panels.
 * The sentence context is rendered with real React nodes (no innerHTML), which
 * removes the old escHtml/XSS surface while reusing taskpane.css mark styles.
 */

import { type ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  ProgressBar,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "../../i18n/useTranslation";

const useStyles = makeStyles({
  card: { display: "flex", flexDirection: "column", gap: "10px" },
  counterRow: { display: "flex", flexDirection: "column", gap: "4px" },
  field: { display: "flex", flexDirection: "column", gap: "2px" },
  label: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  original: { color: tokens.colorPaletteRedForeground1 },
  suggested: { color: tokens.colorPaletteGreenForeground1 },
  actions: { display: "flex", flexWrap: "wrap", gap: "8px" },
});

export function SentenceContext({
  sentence,
  before,
  after,
}: {
  sentence: string;
  before: string;
  after: string;
}): JSX.Element {
  const localIndex = before ? sentence.indexOf(before) : -1;
  if (localIndex === -1) {
    return <span className="sentence-context">{sentence || before}</span>;
  }
  return (
    <span className="sentence-context">
      {sentence.slice(0, localIndex)}
      <mark className="sentence-token sentence-token--before">{before}</mark>
      <span className="sentence-arrow">→</span>
      <mark className="sentence-token sentence-token--after">{after}</mark>
      {sentence.slice(localIndex + before.length)}
    </span>
  );
}

export interface SuggestionCardProps {
  counterText?: string;
  progressPercent?: number;
  sentence: string;
  before: string;
  after: string;
  ruleName: string;
  ruleTooltip?: string;
  onAccept: () => void;
  onSkip: () => void;
  onCancel?: () => void;
  onReport?: () => void;
  onAcceptAllRule?: () => void;
  acceptLabel?: string;
  acceptAllRuleLabel?: string;
  busy?: boolean;
  /* Panel-specific block between the rule metadata and the actions. */
  extraContent?: ReactNode;
}

export function SuggestionCard(props: SuggestionCardProps): JSX.Element {
  const styles = useStyles();
  const { t } = useTranslation();
  const {
    counterText,
    progressPercent,
    sentence,
    before,
    after,
    ruleName,
    ruleTooltip,
    onAccept,
    onSkip,
    onCancel,
    onReport,
    onAcceptAllRule,
    acceptLabel,
    acceptAllRuleLabel,
    busy,
    extraContent,
  } = props;

  return (
    <Card className={styles.card}>
      {counterText ? (
        <div className={styles.counterRow}>
          <Text size={200}>{counterText}</Text>
          {typeof progressPercent === "number" ? (
            <ProgressBar value={progressPercent / 100} />
          ) : null}
        </div>
      ) : null}

      <div className={styles.field}>
        <span className={styles.label}>{t.cardSentence}</span>
        <SentenceContext sentence={sentence} before={before} after={after} />
      </div>

      <div className={styles.field}>
        <span className={styles.label}>{t.cardOriginal}</span>
        <Text className={styles.original}>{before}</Text>
      </div>

      <div className={styles.field}>
        <span className={styles.label}>{t.cardSuggested}</span>
        <Text className={styles.suggested}>{after}</Text>
      </div>

      <div className={styles.field}>
        <span className={styles.label}>{t.cardRule}</span>
        <div
          style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}
        >
          <Badge appearance="outline" title={ruleTooltip}>
            {ruleName}
          </Badge>
          {ruleTooltip ? (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {ruleTooltip}
            </Text>
          ) : null}
        </div>
      </div>

      {extraContent}

      <div className={styles.actions}>
        <Button appearance="primary" onClick={onAccept} disabled={busy}>
          {acceptLabel || t.acceptBtn}
        </Button>
        <Button appearance="secondary" onClick={onSkip} disabled={busy}>
          {t.skipBtn}
        </Button>
        {onCancel ? (
          <Button appearance="subtle" onClick={onCancel} disabled={busy}>
            {t.cancel}
          </Button>
        ) : null}
        {onAcceptAllRule ? (
          <Button appearance="subtle" onClick={onAcceptAllRule} disabled={busy}>
            {acceptAllRuleLabel || t.acceptAllRuleBtn}
          </Button>
        ) : null}
        {onReport ? (
          <Button appearance="transparent" onClick={onReport} disabled={busy}>
            {t.reportFalsePositive}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
